// Launch form rules and initial-buy math. Pure functions (no DOM), so the smoke test runs them in Node too.

export const LIMITS = Object.freeze({ name: 32, symbol: 10, description: 280 });
export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
export const UPLOAD_TYPES = Object.freeze(["image/png", "image/jpeg", "image/webp"]);

export const cleanTicker = (v) => String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, LIMITS.symbol);

export function isHttpUrl(v) {
  const s = String(v || "").trim();
  if (!/^https?:\/\/\S+$/i.test(s)) return false;
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && (u.hostname.includes(".") || u.hostname === "localhost");
  } catch {
    return false;
  }
}
const isDomain = (v) => /^[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(String(v || "").trim());

/** "0.05" → 50000000000000000n; nu/** "0.5" → 500000000n lamports; null when it isn't a positive SOL amount with ≤ 9 decimals. */
export function parseSolAmount(str) {
  const s = String(str ?? "").trim().replace(/,/g, ".");
  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const [i, f = ""] = (s.startsWith(".") ? `0${s}` : s.endsWith(".") ? s.slice(0, -1) : s).split(".");
  if (f.length > 9) return null;
  const v = BigInt(i) * 1000000000n + BigInt((f + "000000000").slice(0, 9));
  return v > 0n ? v : null;
}

/**
 * SOL needed to buy `pct` % of the 1B supply from pump.fun's curve (constant product, virtual reserves
 * V_sol, V_tok): net = V_sol · t / (V_tok − t), grossed up by the trading fee. An estimate.
 */
export function initialBuyFromPercent(pct, curve) {
  const p = Number(pct);
  if (!(p > 0) || p >= 70) return 0n;
  const vs = BigInt(curve?.virtualSolLamports || 0), vt = BigInt(curve?.virtualTokens || 0);
  if (vs <= 0n || vt <= 0n) return 0n;
  const t = (1000000000000000n * BigInt(Math.round(p * 100))) / 10000n; // base units (6 decimals) of the 1B supply
  if (t >= vt) return 0n;
  let lam = (vs * t) / (vt - t);
  const f = BigInt(Math.max(0, Math.min(9999, Math.round(Number(curve?.feeBps) || 0))));
  if (f > 0n) lam = (lam * 10000n) / (10000n - f);
  return lam;
}

/** Initial-buy presets: % of supply (like Mentioned) when the curve shape is known, else plain SOL amounts. */
export function buyPresets(cfg) {
  let ok = false;
  try { ok = BigInt(cfg?.curve?.virtualSolLamports || 0) > 0n; } catch { ok = false; }
  if (ok) return { mode: "percent", options: [{ value: "0", label: "0%" }, { value: "2", label: "2%" }, { value: "5", label: "5%" }, { value: "10", label: "10%" }, { value: "custom", label: "Custom" }] };
  return { mode: "sol", options: [{ value: "0", label: "0 SOL" }, { value: "0.1", label: "0.1" }, { value: "0.5", label: "0.5" }, { value: "1", label: "1" }, { value: "custom", label: "Custom" }] };
}

/** Lamports for the chosen preset; null when the custom amount is invalid. */
export function initialBuyLamports(choice, customAmount, cfg) {
  if (choice === "custom") return parseSolAmount(customAmount);
  if (!choice || choice === "0") return 0n;
  const { mode } = buyPresets(cfg);
  if (mode === "percent") return initialBuyFromPercent(Number(choice), cfg.curve);
  return parseSolAmount(choice) ?? 0n;
}

/**
 * Validates the launch form. values: { name, symbol, logo, description, twitter, telegram, website,
 * buyChoice, buyCustom }. Returns { field: message | null }. The fee split is fixed (70 / 30), so there is
 * nothing to choose or check for it.
 */
export function validateLaunch(values) {
  const v = values || {};
  const name = String(v.name || "").trim();
  const desc = String(v.description || "");
  const e = {};
  e.name = !name ? "Give your coin a name." : name.length > LIMITS.name ? `Keep the name to ${LIMITS.name} characters or fewer.` : null;
  e.symbol = /^[A-Z0-9]{1,10}$/.test(String(v.symbol || "")) ? null : "Ticker: 1–10 characters, A–Z and 0–9.";
  e.logo = v.logo && String(v.logo).trim() && !isHttpUrl(v.logo) ? "Use a full https:// link to a PNG, JPG or WebP image." : null;
  e.description = desc.length > LIMITS.description ? `Keep the description to ${LIMITS.description} characters or fewer.` : null;
  e.twitter = v.twitter && String(v.twitter).trim() && !isHttpUrl(v.twitter) && !/^@?[A-Za-z0-9_]{1,15}$/.test(String(v.twitter).trim()) ? "Enter an X link or @handle." : null;
  e.telegram = v.telegram && String(v.telegram).trim() && !isHttpUrl(v.telegram) && !/^@?[A-Za-z0-9_]{4,32}$/.test(String(v.telegram).trim()) ? "Enter a Telegram link." : null;
  e.website = v.website && String(v.website).trim() && !isHttpUrl(v.website) && !isDomain(v.website) ? "Enter a valid website." : null;
  e.buy = v.buyChoice === "custom" && parseSolAmount(v.buyCustom) === null ? "Enter a SOL amount, like 0.5." : null;
  return e;
}

export const firstError = (errors) => Object.entries(errors || {}).find(([, m]) => m) || null;

/** Normalises the optional links into full URLs. */
export function normaliseLinks({ twitter = "", telegram = "", website = "" }) {
  const t = String(twitter).trim(), g = String(telegram).trim(), w = String(website).trim();
  return {
    twitter: !t ? "" : isHttpUrl(t) ? t : `https://x.com/${t.replace(/^@/, "")}`,
    telegram: !g ? "" : isHttpUrl(g) ? g : `https://t.me/${g.replace(/^@/, "")}`,
    website: !w ? "" : isHttpUrl(w) ? w : `https://${w}`,
  };
}
